import authService from './authService';

import { getServerBaseUrl } from '../config/api';

const API_BASE_URL = getServerBaseUrl();

class AdminService {
  /**
   * Get the current user's JWT token for authentication
   */
  async getIdToken() {
    const token = authService.getToken();
    if (!token) {
      throw new Error('User not authenticated');
    }
    return token;
  }

  /**
   * Impersonate a user using the backend API
   * @param {string} targetUserId - The UID of the user to impersonate
   * @returns {Promise<{customToken: string, targetUser: object, expiresIn: number}>}
   */
  async impersonateUser(targetUserId) {
    try {
      const idToken = await this.getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/api/admin/impersonate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ targetUserId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Admin impersonation error:', error);
      throw new Error(`Failed to impersonate user: ${error.message}`);
    }
  }

  /**
   * Return to admin account using return token (passwordless)
   * @param {string} returnToken - The return token from impersonation
   * @returns {Promise<{customToken: string, adminUser: object}>}
   */
  async returnToAdmin(returnToken) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/return-to-admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ returnToken })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Return to admin error:', error);
      throw new Error(`Failed to return to admin: ${error.message}`);
    }
  }

  /**
   * End impersonation session (optional logging endpoint)
   * @returns {Promise<{success: boolean}>}
   */
  async endImpersonation() {
    try {
      const idToken = await this.getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/api/admin/end-impersonation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('End impersonation error:', error);
      throw new Error(`Failed to end impersonation: ${error.message}`);
    }
  }

  /**
   * Start ngrok tunnel for QuickBooks OAuth
   * @returns {Promise<{success: boolean, url: string}>}
   */
  async startNgrok() {
    try {
      const idToken = await this.getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/api/admin/ngrok/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Start ngrok error:', error);
      throw new Error(`Failed to start ngrok: ${error.message}`);
    }
  }

  /**
   * Stop ngrok tunnel
   * @returns {Promise<{success: boolean}>}
   */
  async stopNgrok() {
    try {
      const idToken = await this.getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/api/admin/ngrok/stop`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Stop ngrok error:', error);
      throw new Error(`Failed to stop ngrok: ${error.message}`);
    }
  }

  /**
   * Check ngrok tunnel status
   * @returns {Promise<{running: boolean, url: string|null}>}
   */
  async getNgrokStatus() {
    try {
      const idToken = await this.getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/api/admin/ngrok/status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Get ngrok status error:', error);
      throw new Error(`Failed to get ngrok status: ${error.message}`);
    }
  }

  /**
   * Get audit logs (admin only)
   * @param {number} limit - Number of logs to retrieve
   * @returns {Promise<{logs: Array, total: number}>}
   */
  async getAuditLogs(limit = 100) {
    try {
      const idToken = await this.getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/api/admin/audit-log?limit=${limit}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Audit log error:', error);
      throw new Error(`Failed to get audit logs: ${error.message}`);
    }
  }

  /**
   * Get OAuth session status
   * @param {string} sessionId - The OAuth session ID
   * @returns {Promise<{success: boolean, franchiseeId: string, completedAt: number, error?: string}>}
   */
  async getOAuthSession(sessionId) {
    try {
      const idToken = await this.getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/qb/session/${sessionId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Get OAuth session error:', error);
      throw new Error(`Failed to get OAuth session: ${error.message}`);
    }
  }

  /**
   * Check if the admin server is healthy
   * @returns {Promise<{status: string, timestamp: string, service: string}>}
   */
  async healthCheck() {
    try {
      const response = await fetch(`${API_BASE_URL}/health`, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Health check error:', error);
      throw new Error(`Admin server health check failed: ${error.message}`);
    }
  }
}

export default new AdminService();